import { useEffect, useState } from "react";
import type { FamilyStudentDetailDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import { fetchFamilyStudentDetail } from "../../shared/api/family";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const familyLinks = [
  { to: "/t/:tenantSlug/home", label: "ホーム" },
  { to: "/t/:tenantSlug/my-schedule", label: "予定" },
  { to: "/t/:tenantSlug/videos", label: "動画" }
];

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

export function StudentDetailPage() {
  const { tenantSlug = "unknown-tenant", studentId = "" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [studentDetail, setStudentDetail] = useState<FamilyStudentDetailDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasParentRole = Boolean(session?.member?.roles.includes("parent"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasParentRole || !studentId) {
      return;
    }

    let cancelled = false;

    async function loadStudent() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextStudentDetail = await fetchFamilyStudentDetail(tenantSlug, studentId);

        if (cancelled) {
          return;
        }

        setStudentDetail(nextStudentDetail);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "生徒詳細の取得に失敗しました");
      }
    }

    void loadStudent();

    return () => {
      cancelled = true;
    };
  }, [hasParentRole, status, studentId, tenantSlug]);

  return (
    <PageScaffold
      title="Student Detail"
      description="linked student の詳細と upcoming schedule を表示します。"
      links={familyLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            family 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock family login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !hasParentRole ? (
          <p>
            parent role が必要です。<Link to={`/t/${tenantSlug}/link-student`}>link-student</Link> から紐付けしてください。
          </p>
        ) : null}

        {loadState === "loading" ? <p>生徒詳細を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {studentDetail ? (
          <>
            <section style={{ display: "grid", gap: 8 }}>
              <strong style={{ fontSize: 20 }}>{studentDetail.student.displayName}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>かな: {studentDetail.student.displayNameKana ?? "未設定"}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>生年月日: {studentDetail.student.birthDate ?? "未設定"}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>予定件数: {studentDetail.student.upcomingCount}</div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>今後の予定</h2>
              {studentDetail.upcomingItems.length === 0 ? <p style={{ margin: 0 }}>表示できる予定はありません。</p> : null}
              {studentDetail.upcomingItems.map((item) => (
                <article
                  key={`${item.itemType}:${item.itemId}`}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 8
                  }}
                >
                  <strong>{item.title}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>{formatDateTime(item.startAt)} - {formatDateTime(item.endAt)}</div>
                  {item.locationLabel ? <div style={{ fontSize: 14 }}>場所: {item.locationLabel}</div> : null}
                  {item.publicNote ? <div style={{ fontSize: 14 }}>お知らせ: {item.publicNote}</div> : null}
                </article>
              ))}
            </section>
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
